import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { catalog } from '@/mocks/home';
import { products } from '@/mocks/products';
import SectionHead from './SectionHead';

gsap.registerPlugin(ScrollTrigger);

const PIN_QUERY = '(min-width: 1024px) and (prefers-reduced-motion: no-preference)';

export default function Catalog() {
  const sectionRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const bar = barRef.current;
    if (!section || !viewport || !track || !bar) return undefined;

    const mm = gsap.matchMedia();
    mm.add(PIN_QUERY, () => {
      const travel = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
      const tl = gsap.timeline({
        scrollTrigger: {
          id: 'catalog-horizontal',
          trigger: section,
          start: 'top top',
          end: () => `+=${travel()}`,
          pin: true,
          pinSpacing: true,
          scrub: 0.6,
          invalidateOnRefresh: true,
        },
      });
      tl.to(track, { x: () => -travel(), ease: 'none' }, 0)
        .fromTo(bar, { scaleX: 0 }, { scaleX: 1, ease: 'none' }, 0);
      return () => {
        gsap.set([track, bar], { clearProps: 'transform' });
      };
    });

    // Re-measure after images load and on resize.
    const imgs = Array.from(track.querySelectorAll('img'));
    // Debounced, and only on real width changes, so refresh never fires
    // mid-scroll for no reason (that re-pin is what caused the jump/flash).
    let timer = 0;
    const refresh = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => ScrollTrigger.refresh(), 200);
    };
    imgs.forEach((img) => { if (!img.complete) img.addEventListener('load', refresh, { once: true }); });
    let lastW = viewport.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = viewport.clientWidth;
      if (w === lastW) return;
      lastW = w;
      refresh();
    });
    ro.observe(viewport);

    return () => {
      window.clearTimeout(timer);
      ro.disconnect();
      imgs.forEach((img) => img.removeEventListener('load', refresh));
      mm.revert();
    };
  }, []);

  // Native-scroll progress for tablet/mobile.
  const onNativeScroll = () => {
    const vp = viewportRef.current;
    const bar = barRef.current;
    if (!vp || !bar || window.matchMedia(PIN_QUERY).matches) return;
    const max = vp.scrollWidth - vp.clientWidth;
    bar.style.transform = `scaleX(${max > 0 ? vp.scrollLeft / max : 1})`;
  };

  return (
    <section id="catalog" ref={sectionRef} className="bg-background-50 overflow-x-clip">
      <div className="lg:min-h-screen flex flex-col justify-center max-w-[1280px] mx-auto px-5 md:px-10 lg:px-12 py-24 md:py-32 lg:py-20">
        <SectionHead motion={false} label="Catalog Preview" title="ONE SOURCE. MORE POSSIBILITIES." intro="Explore a broad selection of valves, fittings and flow-control components for different system requirements." />

        <div
          ref={viewportRef}
          onScroll={onNativeScroll}
          className="mt-12 md:mt-16 overflow-x-auto lg:overflow-visible snap-x snap-mandatory lg:snap-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-5 px-5 md:-mx-10 md:px-10 lg:mx-0 lg:px-0"
        >
          <div ref={trackRef} className="flex gap-6 lg:gap-8 w-max will-change-transform" data-product-shop>
            {catalog.map((p) => (
              <Link key={p.name} to={`/products/${products.find((x) => x.name === p.name)?.slug ?? ''}`} className="group cursor-pointer flex flex-col shrink-0 snap-start w-[200px] sm:w-[240px] lg:w-[260px] xl:w-[280px]">
                <div className="w-full aspect-square flex items-center justify-center p-3">
                  <img src={p.image} alt={`${p.name} - ${p.family}`} title={`${p.name} - NORTHLINE`} className="max-h-full max-w-full object-contain object-center transition-transform duration-700 group-hover:scale-[1.03]" />
                </div>
                <h3 className="mt-4 t-body text-foreground-950 group-hover:text-primary-500 transition-colors">{p.name}</h3>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10 h-px w-full bg-background-200 overflow-hidden">
          <div ref={barRef} className="h-full w-full bg-primary-500 origin-left" style={{ transform: 'scaleX(0)' }} />
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-sm text-foreground-600">Representative product forms. Availability and specifications are confirmed with each quotation.</p>
          <a href="#quote" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 t-body text-foreground-950 hover:text-primary-500 transition-colors">Ask about a component <i className="ri-arrow-right-line" /></a>
        </div>
      </div>
    </section>
  );
}